import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BarChart2, Target, TrendingUp, Users, Activity, Clock, Zap } from 'lucide-react';

// Import Offensive Intelligence Components
import ShotValueMap from '../../components/basketball/ShotValueMap';
import LineupOptimizer from '../../components/basketball/LineupOptimizer';
import EndGameDecision from '../../components/basketball/EndGameDecision';
import DemoModeBanner from '../../components/DemoModeBanner';

/**
 * BasketballAnalytics - Offensive Intelligence Dashboard
 *
 * SLAM BALL's core analytics module featuring:
 * - Shot selection optimization (Shot Value Map)
 * - Lineup analysis and optimization
 * - End-game decision support
 * - Team efficiency metrics
 */
export default function BasketballAnalytics() {
  const context = useOutletContext();

  const [activeTab, setActiveTab] = useState('shots');

  // Basketball-specific metrics
  const teamMetrics = useMemo(() => [
    {
      id: 'offensiveRating',
      label: 'Offensive Rating',
      value: '115.2',
      trend: '+3.1',
      trendUp: true,
      description: 'Points per 100 possessions',
    },
    {
      id: 'defensiveRating',
      label: 'Defensive Rating',
      value: '108.4',
      trend: '-2.3',
      trendUp: true,
      description: 'Points allowed per 100 possessions',
    },
    {
      id: 'netRating',
      label: 'Net Rating',
      value: '+6.8',
      trend: '+5.4',
      trendUp: true,
      description: 'Offensive - Defensive Rating',
    },
    {
      id: 'pace',
      label: 'Pace',
      value: '101.3',
      trend: '+1.2',
      trendUp: true,
      description: 'Possessions per 48 minutes',
    },
  ], []);

  // Tab navigation - now includes Offensive Intelligence features
  const tabs = [
    { id: 'shots', label: 'Shot Selection', icon: <Target size={18} />, description: 'Shot value by zone' },
    { id: 'lineups', label: 'Lineups', icon: <Users size={18} />, description: '5-man efficiency' },
    { id: 'endgame', label: 'End-Game', icon: <Clock size={18} />, description: 'Clutch decisions' },
    { id: 'overview', label: 'Overview', icon: <BarChart2 size={18} />, description: 'Team metrics' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#F5F5F5]">
            Offensive Intelligence
          </h1>
          <p className="mt-1 text-[#999999]">
            Shot selection, lineup optimization, and end-game decisions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="text-[#E8B800]" size={24} />
          <span className="text-sm font-medium text-[#E8B800]">
            SLAM BALL
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-2 border-[#1A1A1A]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-[#E8B800] text-[#0A0A0A]'
                : 'text-[#999999] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#F5F5F5]'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Shot Selection Tab */}
      {activeTab === 'shots' && (
        <div className="p-6 rounded-[14px] border bg-[#111111] border-[#1A1A1A]">
          <ShotValueMap
            playerId="player-1"
            playerName="Team Average"
          />
        </div>
      )}

      {/* Lineups Tab */}
      {activeTab === 'lineups' && (
        <div className="p-6 rounded-[14px] border bg-[#111111] border-[#1A1A1A]">
          <LineupOptimizer teamId="real-madrid" />
        </div>
      )}

      {/* End-Game Tab */}
      {activeTab === 'endgame' && (
        <div className="p-6 rounded-[14px] border bg-[#111111] border-[#1A1A1A]">
          <EndGameDecision />
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <DemoModeBanner compact message="Team metrics (ORtg, DRtg, Net, Pace) use placeholder values. Connect live stats feed for real data." />
          {/* Team Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {teamMetrics.map((metric) => (
              <div
                key={metric.id}
                className="p-5 rounded-[14px] border bg-[#111111] border-[#1A1A1A]"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#999999]">
                      {metric.label}
                    </p>
                    <p className="text-3xl font-bold mt-1 text-[#F5F5F5]">
                      {metric.value}
                    </p>
                    <p className="text-xs mt-1 text-[#555555]">
                      {metric.description}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1 text-sm">
                  <TrendingUp
                    size={14}
                    className={metric.trendUp ? 'text-green-500' : 'text-red-500'}
                  />
                  <span className={metric.trendUp ? 'text-green-500' : 'text-red-500'}>
                    {metric.trend}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Efficiency Summary */}
          <div className="p-6 rounded-[14px] border bg-[#111111] border-[#1A1A1A]">
            <h2 className="text-lg font-semibold mb-4 text-[#F5F5F5]">
              Offensive Intelligence Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 rounded-lg bg-[#1A1A1A]">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="text-[#E8B800]" size={20} />
                  <h3 className="font-medium text-[#F5F5F5]">
                    Shot Selection
                  </h3>
                </div>
                <ul className="text-sm space-y-1 text-[#999999]">
                  <li>Best zone: Corner 3 (1.29 PPP)</li>
                  <li>Avoid: Mid-range (0.71 PPP)</li>
                  <li>Recommendation: +15% rim attempts</li>
                </ul>
              </div>

              <div className="p-4 rounded-lg bg-[#1A1A1A]">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="text-[#E8B800]" size={20} />
                  <h3 className="font-medium text-[#F5F5F5]">
                    Lineup Insights
                  </h3>
                </div>
                <ul className="text-sm space-y-1 text-[#999999]">
                  <li>Best lineup: +0.14 Net PPP</li>
                  <li>Top synergy: Llull-Tavares (92)</li>
                  <li>Avoid: Alocen + Campazzo together</li>
                </ul>
              </div>

              <div className="p-4 rounded-lg bg-[#1A1A1A]">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="text-[#E8B800]" size={20} />
                  <h3 className="font-medium text-[#F5F5F5]">
                    End-Game
                  </h3>
                </div>
                <ul className="text-sm space-y-1 text-[#999999]">
                  <li>Up 3: Don't foul until 4s</li>
                  <li>Down 2: Go for 3PT (34% win)</li>
                  <li>Best closer: Llull (42% clutch)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setActiveTab('shots')}
              className="p-4 rounded-[14px] text-left transition-all hover:scale-[1.02] bg-[#111111] border border-[#1A1A1A] hover:border-[#E8B800]/50"
            >
              <Target className="text-[#E8B800] mb-2" size={24} />
              <h3 className="font-semibold text-[#F5F5F5]">
                Shot Value Map
              </h3>
              <p className="text-sm text-[#999999]">
                Analyze shot efficiency by zone
              </p>
            </button>

            <button
              onClick={() => setActiveTab('lineups')}
              className="p-4 rounded-[14px] text-left transition-all hover:scale-[1.02] bg-[#111111] border border-[#1A1A1A] hover:border-[#E8B800]/50"
            >
              <Users className="text-[#E8B800] mb-2" size={24} />
              <h3 className="font-semibold text-[#F5F5F5]">
                Lineup Optimizer
              </h3>
              <p className="text-sm text-[#999999]">
                Find best 5-man combinations
              </p>
            </button>

            <button
              onClick={() => setActiveTab('endgame')}
              className="p-4 rounded-[14px] text-left transition-all hover:scale-[1.02] bg-[#111111] border border-[#1A1A1A] hover:border-[#E8B800]/50"
            >
              <Clock className="text-[#E8B800] mb-2" size={24} />
              <h3 className="font-semibold text-[#F5F5F5]">
                End-Game Engine
              </h3>
              <p className="text-sm text-[#999999]">
                Clutch decision support
              </p>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
